import React, { useEffect } from 'react';
import { Modal, Form, Input, notification, Button } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { PatCreateRequest, PatCreateResponse } from '@/services/identity/pat.service';
import { logError } from '@/appInsights';

const createTokenSchema = yup.object({
  name: yup
    .string()
    .required('Token name is required')
    .min(1, 'Token name must be at least 1 character')
    .max(100, 'Token name must be at most 100 characters'),
});

type CreateTokenFormData = {
  name: string;
};

export interface PersonalAccessTokenCreateModalProps {
  visible: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: PatCreateRequest) => Promise<PatCreateResponse>;
}

export const PersonalAccessTokenCreateModal: React.FC<PersonalAccessTokenCreateModalProps> = ({
  visible,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateTokenFormData>({
    mode: 'onTouched',
    resolver: yupResolver(createTokenSchema),
    defaultValues: { name: '' },
  });

  const handleFormSubmit = handleSubmit(
    async (formData) => {
      try {
        await onSubmit({ name: formData.name });
        reset();
        onClose();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create token';
        logError(errorMessage, { action: 'create_token_modal' });
        notification.error({ message: t('common.error'), description: errorMessage });
      }
    },
    () => {
      notification.error({
        message: t('common.error'),
        description: t('common.patRequiredFields') || 'Please check all required fields',
      });
    },
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!visible) {
      reset();
    }
  }, [visible, reset]);

  return (
    <Modal
      title={t('common.patCreateToken') || 'Create Personal Access Token'}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key='cancel' onClick={handleClose}>
          {t('common.cancel') || 'Cancel'}
        </Button>,
        <Button key='submit' type='primary' loading={loading} onClick={handleFormSubmit}>
          {t('common.create')}
        </Button>,
      ]}
    >
      <Form form={form} layout='vertical'>
        <Controller
          name='name'
          control={control}
          render={({ field }) => (
            <Form.Item
              label={t('common.patTokenName') || 'Token Name'}
              validateStatus={errors.name ? 'error' : ''}
              help={errors.name?.message}
              required
            >
              <Input
                {...field}
                placeholder={t('common.patEnterTokenName') || 'Enter token name'}
                disabled={loading}
                autoFocus
              />
            </Form.Item>
          )}
        />
      </Form>
    </Modal>
  );
};
